import { Component, Inject, PLATFORM_ID, Renderer2, ViewEncapsulation } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { Brand } from '../../models/brand';
import { DOCUMENT, NgFor, NgIf } from '@angular/common';
import { Category } from '../../models/category';
import { BrandApiService } from '../../services/brand-api.service';
import { CategoryApiService } from '../../services/category-api.service';
import { PagesApiService } from '../../services/pages-api.service';
import { PageMeta } from '../../models/page-meta';
import { ReplaceMetaDatePipe } from '../../pipes/replace-meta-date.pipe';

@Component({
  selector: 'app-all-brands',
  imports: [NgIf, NgFor, RouterModule],
  templateUrl: './all-brands.component.html',
  styleUrl: './all-brands.component.css',
  encapsulation: ViewEncapsulation.None
})
export class AllBrandsComponent {
  brands: Brand[] = [];
  brandName = '';
  brandId = 0;
  topCategories: Category[] = [];
  today = new Date();
  currentYear: number = 0;
  currentDate: Date = new Date();
  totalOffersCount: number = 0;
  topBrands: Brand[] = [];
  pendingCalls: number = 0;
  isDataLoaded: boolean = false;
  pagemeta: PageMeta[] = [];

  constructor(
    private brandApi: BrandApiService,
    private categoryApi: CategoryApiService,
    private title: Title,
    private meta: Meta,
    private replaceMetaPipe: ReplaceMetaDatePipe,
    private pageMetaApi: PagesApiService,
    @Inject(DOCUMENT) private dom: Document,
    private renderer: Renderer2
  ) {
    this.currentYear = this.today.getFullYear();
    this.activeLetter = 'A';
  }

  ngOnInit() {
    this.loadAllData();
  }

  loadAllData() {
    this.pendingCalls = 1;
    this.getStores();
    //this.getTopCategories();
    this.getPageMeta();
    //this.getTopBrands();
  }

  checkIfAllDone() {
    this.pendingCalls--;
    if (this.pendingCalls === 0) {
      this.isDataLoaded = true;
    }
  }

  public loadScript(url: string) {
    const body = <HTMLDivElement>document.body;
    const script = document.createElement('script');
    script.innerHTML = '';
    script.src = url;
    script.async = false;
    script.defer = true;
    body.appendChild(script);
  }

  getStores() {
    this.brandApi.getActiveStores().subscribe((data) => {
      if (data.Message === 'Data Fetched Successfully!!!') {
        this.brands = data.Data;
        this.groupAndSortBrands();
        this.isDataLoaded = true;
      } else if (data.Message === 'No Records Found') {
        this.brands = [];
      } else {
      }
      this.checkIfAllDone();
    });
  }

  getTopCategories() {
    this.categoryApi.getTopCategories(1).subscribe((data) => {
      if (data.Message === 'Data Fetched Successfully!!!') {
        this.topCategories = data.Data as Category[];
      } else {
      }
      this.checkIfAllDone();
    });
  }

  getTopBrands() {
    this.brandApi.getStores(1, 1, 10).subscribe((data) => {
      if (data.Message === 'Data Fetched Successfully!!!') {
        this.topBrands = data.Data;
      } else {
      }
      this.checkIfAllDone();
    });
  }

  getPageMeta() {
    this.pageMetaApi.getPagesMeta().subscribe((data) => {
      if (data.Message === 'Data Fetched Successfully!!!') {
        this.pagemeta = data.Data;
        this.updateMetaData();
      } else if (data.Message === 'No Records Found') {
        this.pagemeta = [];
      } else {
      }
      this.checkIfAllDone();
    });
  }

  updateMetaData() {
    const pageMetaData = this.pagemeta.find(
      (p) => p.PageName === 'all_stores' && p.IsActive
    );
    if (pageMetaData!.PageId > 0) {
      const pageTitle = this.replaceMetaPipe.transform(pageMetaData!.PageTitle!);
      const pageDescription = this.replaceMetaPipe.transform(pageMetaData!.PageDescription!);
      const pageUrl = `https://www.dealszo.com/stores/`;
      // const keyWords = pageMetaData!.PageKeywords!;

      // Update the title and meta tags dynamically
      this.title.setTitle(pageTitle);
      this.meta.updateTag({ name: 'title', content: pageTitle });
      this.meta.updateTag({ name: 'description', content: pageDescription });
      // this.meta.updateTag({
      //   name: 'keywords',
      //   content: keyWords,
      // });

      this.meta.updateTag({
        name: 'robots',
        content:
          'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
      });

      // Open Graph meta tags
      this.meta.updateTag({ property: 'og:type', content: 'website' });
      this.meta.updateTag({ property: 'og:site_name', content: 'dealszo.com' });
      this.meta.updateTag({ property: 'og:title', content: pageTitle });
      this.meta.updateTag({
        property: 'og:description',
        content: pageDescription,
      });
      this.meta.updateTag({ property: 'og:url', content: pageUrl });

      // Canonical URL and other necessary meta tags
      let link: HTMLLinkElement = this.dom.querySelector(
        `link[rel='canonical']`
      )!;
      let alternativeLink: HTMLLinkElement = this.dom.querySelector(
        `link[rel='alternate']`
      )!;
      if (link) {
        this.renderer.setAttribute(link, 'href', pageUrl);
      } else {
        link = this.renderer.createElement('link');
        this.renderer.setAttribute(link, 'rel', 'canonical');
        this.renderer.setAttribute(link, 'href', pageUrl);
        this.renderer.appendChild(this.dom.head, link);
      }
      if (alternativeLink) {
        this.renderer.setAttribute(alternativeLink, 'href', pageUrl);
      } else {
        link = this.renderer.createElement('link');
        this.renderer.setAttribute(alternativeLink, 'rel', 'canonical');
        this.renderer.setAttribute(alternativeLink, 'href', pageUrl);
        this.renderer.appendChild(this.dom.head, link);
      }
    }

    if (pageMetaData!.PageSchema && pageMetaData!.PageSchema.trim() !== '') {
      const schema = this.replaceMetaPipe.transform(pageMetaData!.PageSchema);
      const jsonLdScripts = this.dom.head.querySelectorAll(
        'script[type="application/ld+json"]'
      );
      jsonLdScripts.forEach(script => script.remove());
      const temp = this.dom.createElement('div');
      temp.innerHTML = schema;
      const scripts = temp.querySelectorAll(
        'script[type="application/ld+json"]'
      );
      scripts.forEach((scriptTag, index) => {
        const newScript = this.dom.createElement('script');
        newScript.type = 'application/ld+json';
        newScript.id = `schema-jsonld-${index}`;
        newScript.text = scriptTag.textContent || '';
        this.dom.head.appendChild(newScript);
      });
    }
  }

  groupedBrands: { [key: string]: Brand[] } = {};
  groupedBrandKeys: string[] = [];
  originalGroupedBrands: { [key: string]: Brand[] } = {};

  groupAndSortBrands() {
    this.groupedBrands = {};
    this.groupedBrandKeys = [];

    this.brands.forEach((brand) => {
      const trimmedBrandName = brand.StoreName.trim();
      this.totalOffersCount += brand.DealCount + brand.CouponCount;
      const firstChar = trimmedBrandName.charAt(0).toUpperCase();
      if (
        trimmedBrandName.length === 0 ||
        /^[0-9]/.test(firstChar) ||
        /[^a-zA-Z0-9]/.test(firstChar)
      ) {
        if (!this.groupedBrands['123']) this.groupedBrands['123'] = [];
        this.groupedBrands['123'].push(brand);
      } else {
        if (!this.groupedBrands[firstChar]) this.groupedBrands[firstChar] = [];
        this.groupedBrands[firstChar].push(brand);
      }
    });
    Object.keys(this.groupedBrands).forEach((key) => {
      this.groupedBrands[key].sort((a, b) =>
        a.StoreName.localeCompare(b.StoreName)
      );
    });
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const sortedKeys = [];
    if (this.groupedBrands['123']) {
      sortedKeys.push('123');
    }
    alphabet.split('').forEach((letter) => {
      if (this.groupedBrands[letter]) {
        sortedKeys.push(letter);
      }
    });
    this.groupedBrandKeys = sortedKeys;
    this.originalGroupedBrands = this.groupedBrands;
  }

  activeLetter: string = '';
  showAll: boolean = true;
  alphabetLetters: string[] = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  // Method to change active tab
  setActiveTab(tab: string) {
    this.groupedBrands = {};
    this.activeLetter = tab;
    if (tab == 'all') {
      this.showAll = true;
      this.groupedBrands = this.originalGroupedBrands;
    } else {
      this.showAll = false;
      this.groupedBrands[tab] = this.originalGroupedBrands[tab];
    }
  }

  openCouponsPage(brand: Brand) {
    if (brand && brand.SeoName) {
      window.location.href = `${brand.SeoName}-coupons/`;
      // this.router.navigate([`${brand.SeoName}-coupons/`], {
      //   state: { name: brand.StoreName, id: brand.StoreId },
      // });
    } else {
      console.error('Brand is undefined or invalid:', brand);
    }
  }
}
